import { useMemo } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";

const pillars = [
  { icon: "ri-eye-line", title: "看得见的热点", desc: "每一个热点都整理出清晰脉络，让事件来龙去脉一目了然。" },
  { icon: "ri-flashlight-fill", title: "跟得上的速度", desc: "每日持续更新，最新热点上线即汇总，不让你错过任何现场。" },
  { icon: "ri-shield-check-line", title: "信得过的内容", desc: "信息交叉核对、来源可溯，拒绝为了流量而夸大和失真。" },
];

const principles = [
  "先核实，后发布：对热点信息进行多方交叉验证，不抢跑、不传谣。",
  "讲清楚，不堆砌：还原事件完整脉络，而不是简单罗列碎片。",
  "守边界，有温度：尊重当事人，理性吃瓜，不传播无依据的负面猜测。",
  "重质量，可持续：坚持每日更新，但绝不为更新而牺牲内容质量。",
];

const voices = [
  { name: "追剧小李", tag: "上班族", text: "每天午休刷一下，今天发生了什么全知道了，特省心。" },
  { name: "八卦老八", tag: "吃瓜老手", text: "一个事件一个专题，来龙去脉讲得清楚，比我自己扒强多了。" },
  { name: "前排观众", tag: "忠实用户", text: "更新真的快，热搜刚出来就有视频汇总了，收藏了天天来看。" },
];

export default function AboutPage() {
  const jsonLd = useMemo(() => {
    const base = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于吃瓜速递",
        url: `${base}/about`,
        description: "了解吃瓜速递的品牌介绍、网站定位、发展理念、内容原则与联系方式。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: base },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${base}/about` },
        ],
      },
    ];
  }, []);

  return (
    <>
      <Seo
        title="关于我们 - 吃瓜速递 · 国产吃瓜视频热点资讯站"
        description="了解吃瓜速递的品牌介绍、网站定位、发展理念、内容原则与联系方式。我们专注国产吃瓜视频最新热点与八卦汇总，每日更新，靠谱速递。"
        keywords="国产吃瓜视频,关于我们,网站介绍,发展理念,内容原则,吃瓜速递"
        path="/about"
        jsonLd={jsonLd}
      />

      <div className="bg-foreground-950 pb-14 pt-24 md:pt-28">
        <div className="container-x">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 text-sm text-background-50/60">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">关于吃瓜速递</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/75">
            一个专注国产吃瓜视频最新热点与八卦汇总的资讯速递站，用清晰、及时、靠谱的内容，陪你看懂每一场热闹。
          </p>
        </div>
      </div>

      <section className="container-x py-10 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
              <i className="ri-focus-2-line"></i>
              品牌介绍
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
              我们是谁
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              吃瓜速递是一个面向广大网民的吃瓜热点视频资讯站。我们聚合明星动态、娱乐八卦、综艺现场、网络红人与社会热点等热门话题，以「一个事件一个专题」的方式，把最新、最热、最完整的吃瓜内容汇总给每一位关注热点的朋友。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              我们相信，追热点不该是一地鸡毛的碎片，而应是清晰好懂的故事。因此从上线起，我们就坚持每日更新、持续速递，让用户每天刷新本站，都能第一时间掌握近期最值得关注的热点事件。
            </p>

            <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {pillars.map((p) => (
                <div key={p.title} className="card p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                    <i className={p.icon}></i>
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-900">{p.title}</h3>
                  <p className="mt-1.5 text-xs leading-5 text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600">
              <i className="ri-compass-3-line"></i>
              定位与理念
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
              我们想成为什么样
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              我们的定位，是做一个值得信赖的「吃瓜内容综合入口」。无论是新晋吃瓜群众，还是资深八卦老手，都能在这里快速定位到感兴趣的话题，不必在多个平台之间反复横跳。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              发展理念很简单：以内容为核心，以用户为先。我们坚持让内容自己说话，用真实的脉络和多元的视角，帮助每一位访客在热闹中保持清醒、在信息里抓住重点。
            </p>

            <div className="card mt-7 p-5">
              <h3 className="text-sm font-semibold text-foreground-900">我们的内容原则</h3>
              <ul className="mt-4 space-y-3">
                {principles.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-6 text-foreground-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-bold text-secondary-700">
                      {i + 1}
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100/70 py-10 md:py-14">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary-600">
              <i className="ri-chat-quote-line"></i>
              用户口碑
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">
              来自爱吃瓜的你
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="card p-5">
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-6 text-foreground-700">“{v.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {v.name[0]}
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{v.name}</span>
                  <span className="chip ml-1">{v.tag}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-10 md:py-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="card bg-primary-100/70 p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-mail-send-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-2 text-sm leading-6 text-foreground-700">
              有想看的吃瓜事件、想反馈的建议或合作意向，欢迎通过邮箱与我们联系，我们会认真查看每一条信息。
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <i className="ri-mail-line text-primary-600"></i>
                contact@chiguasudi.com
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <i className="ri-chat-smile-3-line text-primary-600"></i>
                反馈栏目：内容、更新、合作
              </li>
            </ul>
          </div>

          <div className="card p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-600">
              <i className="ri-question-mark text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950">还有疑问？</h2>
            <p className="mt-2 text-sm leading-6 text-foreground-700">
              想了解更新频率、内容覆盖范围或使用方式，可以在常见问题页面找到答案。
            </p>
            <Link to="/faq" className="btn-primary mt-5">
              查看常见问题
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}